import React, { useEffect } from 'react';
import { Mail, Calendar, Brain, Zap, TrendingUp } from 'lucide-react';
import { Button } from './shadcn/button';
import { Input } from './shadcn/input';

// Only import Supabase when needed to avoid initialization errors
let supabase;
const initializeSupabase = () => {
  if (!supabase) {
    try {
      // Dynamically import the Supabase client
      const supabaseModule = require('../lib/supabaseClient');
      supabase = supabaseModule.default;
    } catch (error) {
      // Fallback implementation
      supabase = {
        from: () => ({
          insert: (data) => {
            return Promise.resolve({ data: null, error: null });
          }
        })
      };
    }
  }
  return supabase;
};

function ComingSoon() {
  const [email, setEmail] = React.useState('');
  const [submitted, setSubmitted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');

  // Set body background to match the page background
  useEffect(() => {
    document.body.style.background = 'linear-gradient(to bottom right, #eff6ff, #c7d2fe)';
    document.body.style.backgroundAttachment = 'fixed';
    
    // Cleanup function to reset body styles when component unmounts
    return () => {
      document.body.style.background = '';
      document.body.style.backgroundAttachment = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    // Basic email validation
    if (!email || !/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }
    
    // Initialize Supabase when needed
    const supabaseClient = initializeSupabase();
    
    try {
      const { error } = await supabaseClient
        .from('waitlist_subscribers')
        .insert([{ email }]);
      
      if (error) throw error;
      
      setSubmitted(true);
      setEmail('');
    } catch (err) {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen min-h-[100dvh] bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      {/* Header */}
      <header className="py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <img src="/logo.png" alt="NeuralVision Logo" className="h-10" />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow flex items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="max-w-4xl w-full text-center">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-8">
            <Brain className="h-12 w-12 text-white" />
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            NeuralVision TMC
          </h1>
          
          <p className="text-xl text-gray-600 mb-4 max-w-3xl mx-auto">
            Revolutionary AI-Powered Traffic Movement Count Analysis Platform
          </p>
          
          <p className="text-lg text-gray-500 mb-12 max-w-3xl mx-auto">
            Leveraging cutting-edge computer vision and deep learning algorithms to transform traffic video analysis for transportation professionals
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">AI-Powered Precision</h3>
              <p className="text-gray-600">
                Advanced neural networks deliver 97% accuracy in vehicle detection and classification
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-indigo-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <TrendingUp className="h-8 w-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Data-Driven Insights</h3>
              <p className="text-gray-600">
                Generate actionable intelligence from traffic patterns with real-time analytics
              </p>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="bg-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Rapid Turnaround</h3>
              <p className="text-gray-600">
                Process traffic videos in minutes, not hours, with our optimized infrastructure
              </p>
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-2xl shadow-xl p-8 mb-12 text-left text-white">
            <h2 className="text-2xl font-bold mb-6 text-center">Platform Capabilities:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-blue-500 rounded-full p-1">
                    <div className="bg-white rounded-full w-2 h-2"></div>
                  </div>
                </div>
                <p className="ml-3">Deep learning algorithms for vehicle detection and classification</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-blue-500 rounded-full p-1">
                    <div className="bg-white rounded-full w-2 h-2"></div>
                  </div>
                </div>
                <p className="ml-3">Automated timestamp extraction from video metadata</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-blue-500 rounded-full p-1">
                    <div className="bg-white rounded-full w-2 h-2"></div>
                  </div>
                </div>
                <p className="ml-3">Multi-format report generation (TSV, Excel)</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-blue-500 rounded-full p-1">
                    <div className="bg-white rounded-full w-2 h-2"></div>
                  </div>
                </div>
                <p className="ml-3">Enterprise-grade security and compliance</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-blue-500 rounded-full p-1">
                    <div className="bg-white rounded-full w-2 h-2"></div>
                  </div>
                </div>
                <p className="ml-3">Scalable infrastructure for high-volume processing</p>
              </div>
              <div className="flex items-start">
                <div className="flex-shrink-0 mt-1">
                  <div className="bg-blue-500 rounded-full p-1">
                    <div className="bg-white rounded-full w-2 h-2"></div>
                  </div>
                </div>
                <p className="ml-3">API integration for seamless workflow automation</p>
              </div>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-2xl shadow-lg p-8 mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Privacy Compliance</h2>
            <p className="text-gray-700 mb-4">
              We are committed to protecting your privacy and ensuring compliance with international data protection regulations:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 border border-green-100">
                <h3 className="font-bold text-gray-900 mb-2">PIPEDA (Canada)</h3>
                <p className="text-sm text-gray-600">
                  Compliant with Canada's Personal Information Protection and Electronic Documents Act
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-100">
                <h3 className="font-bold text-gray-900 mb-2">GDPR (EU)</h3>
                <p className="text-sm text-gray-600">
                  Fully compliant with the European Union's General Data Protection Regulation
                </p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-green-100">
                <h3 className="font-bold text-gray-900 mb-2">CCPA (California)</h3>
                <p className="text-sm text-gray-600">
                  Compliant with California's Consumer Privacy Act
                </p>
              </div>
            </div>
            <p className="text-gray-700 text-sm">
              For detailed information about our privacy practices, please contact us.
            </p>
          </div>

          <div className="max-w-md mx-auto">
            <h3 className="text-2xl font-semibold text-gray-900 mb-4">Get notified at launch</h3>
            <p className="text-gray-600 mb-6">
              Subscribe to our waiting list for exclusive early access and special launch pricing
            </p>
            
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium">
                  You're on the list!
                </p>
                <p className="text-green-700 text-sm mt-1">
                  We've added you to our waitlist and will notify you when NeuralVision launches.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                <div className="flex-grow relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <Input
                    type="email"
                    id="email"
                    className="pl-10"
                    placeholder="Enter your work email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                  />
                </div>
                <Button 
                  type="submit"
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white whitespace-nowrap"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    'Join Waiting List'
                  )}
                </Button>
              </form>
            )}
            {error && (
              <div className="mt-2 text-red-600 text-sm">
                {error}
              </div>
            )}
            
            <p className="mt-3 text-sm text-gray-500">
              We respect your privacy. Unsubscribe at any time. No spam, ever.
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} NeuralVision Technologies. All rights reserved.
          </p>
          <p className="text-center text-gray-400 text-xs mt-2">
            Patent Pending. NeuralVision is a trademark of NeuralVision Technologies.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default ComingSoon;